import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

import { UniqueTvChart } from "../../components/charts3";
import { ChartControls } from "../../components/Channel";

import { useUniqueTvData } from "../../hooks/useUniqueTvData";
import { useDaysSorted } from "../../hooks/useDaysSorted";
import { useDateInputs } from "../../hooks/useDateInputs";
import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";

import { useUniqueTvSeries } from "../../hooks/useUniqueTvSeries";

export default function ChannelUniqueTv({ channelId, refreshKey, refreshEnabled, setRefreshEnabled, refreshIntervalMin, setRefreshIntervalMin, refreshCountdown, manualRefresh, rawVideo }) {
  const MODE = "day";

  // --- LOGGER INTEGRATION ---
  const { logPageChange } = useUserJourneyLogger({
    channelId,
    // channelName is not available here, so omit or set to undefined
  });

  useEffect(() => {
    logPageChange({
      chartType: "uniquetv",
    });
  }, [logPageChange]);

  // rawVideo is received as a prop
  const sortedKeys = useDaysSorted(rawVideo);

  const yesterdayKey = sortedKeys?.at(-2) ?? null;
  const dates = useDateInputs(sortedKeys, MODE);

  const defaultRange = useMemo(() => {
    if (!yesterdayKey) return { start: "", end: "" };
    const end = dayjs(yesterdayKey, "DD-MM-YYYY");
    return {
      start: end.subtract(6, "day").format("YYYY-MM-DD"),
      end: end.format("YYYY-MM-DD"),
    };
  }, [yesterdayKey]);

  const [calendarTouched, setCalendarTouched] = useState(false);

  useEffect(() => {
    setCalendarTouched(false);
  }, [defaultRange.start, defaultRange.end]);

  const effectiveStart = calendarTouched ? dates.startDateInput : defaultRange.start;
  const effectiveEnd = calendarTouched ? dates.endDateInput : defaultRange.end;

  // ✅ CRITICAL: fetch unique tv for the selected range
  const { data: uniqueTvData, loading } = useUniqueTvData(
    channelId,
    effectiveStart,
    effectiveEnd,
    refreshKey
  );

  const series = useUniqueTvSeries({
    uniqueTvData,
    startDateInput: effectiveStart,
    endDateInput: effectiveEnd,
  });

  return (
    <>
      <ChartControls
        channelId={channelId}
        mode={MODE}
        hideModeButtons
        startDateInput={effectiveStart}
        endDateInput={effectiveEnd}
        onStartDateChange={(v) => {
          dates.setStartDateInput(v);
          setCalendarTouched(true);
        }}
        onEndDateChange={(v) => {
          dates.setEndDateInput(v);
          setCalendarTouched(true);
        }}
        refreshEnabled={refreshEnabled}
        onRefreshToggle={setRefreshEnabled}
        refreshIntervalMin={refreshIntervalMin}
        onRefreshIntervalChange={setRefreshIntervalMin}
        countdown={refreshCountdown}
        onManualRefresh={manualRefresh}
      />

      <div className="chart-box">

        {series.length > 0 ? (
          <UniqueTvChart
            series={series}
            mode="day"
            chartType="line"
            loading={loading}
            refreshKey={refreshKey}
          />
        ) : (
          <div style={{ padding: 40, opacity: 0.4 }}>(no data)</div>
        )}
      </div>
    </>
  );
}
